import {
  BarChart3,
  CheckCircle,
  Clock,
  Download,
  Eye,
  FileText,
  Filter,
  Mic,
  Play,
  Plus,
  Search,
  TrendingUp,
  Trash2,
  Upload,
  X
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useJourney } from '../../contexts/JourneyContext';
import api from '../../services/api';
import { Resume, ResumeJourney, Stage } from '../../types';
import './Dashboard.css';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setCurrentJourney } = useJourney();
  const [journeys, setJourneys] = useState<ResumeJourney[]>([]);
  const [resumes, setResumes] = useState<Resume[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination state
  const [journeysPage, setJourneysPage] = useState(1);
  const [resumesPage, setResumesPage] = useState(1);
  const itemsPerPage = 3;

  // Filter & Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<'all' | 'today' | 'week' | 'month'>('all');
  const [showFilters, setShowFilters] = useState(false);
  
  // Bulk actions state
  const [selectedResumes, setSelectedResumes] = useState<Set<string>>(new Set());
  const [bulkActionMode, setBulkActionMode] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [journeysData, resumesData] = await Promise.all([
        api.listJourneys(),
        api.listResumes()
      ]);

      setJourneys(journeysData.journeys || []);
      setResumes(resumesData.resumes || []);
    } catch (err: any) {
      console.error('Failed to load dashboard:', err);
      setError('Failed to load your data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResumeJourney = (journey: ResumeJourney) => {
    setCurrentJourney(journey);
    
    // Navigate based on current stage
    if (journey.currentStage === Stage.PersonalInfo) {
      navigate('/journey/forms/personal');
    } else if (journey.currentStage === Stage.Education) {
      navigate('/journey/forms/education');
    } else if (journey.currentStage === Stage.EmploymentHistory) {
      navigate('/journey/forms/employment');
    } else if (journey.currentStage === Stage.Projects || journey.currentStage === Stage.SkillsCertifications) {
      navigate('/journey/stages');
    } else if (journey.currentStage === Stage.Review) {
      navigate('/journey/review');
    } else if (journey.currentStage === Stage.TemplateSelection) {
      navigate('/journey/templates');
    } else {
      navigate('/journey/stages');
    }
  };

  const handleViewResume = (resumeId: string) => {
    navigate(`/resume/${resumeId}`);
  };

  const handleCheckATS = (resumeId: string) => {
    navigate(`/ats-checker/${resumeId}`);
  };

  const handleDownloadResume = async (resumeId: string, resumeName: string) => {
    try {
      const blob = await api.downloadResume(resumeId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${resumeName}_resume.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download resume:', err);
      alert('Failed to download resume. Please try again.');
    }
  };

  const getStageLabel = (stage: Stage): string => {
    const labels: { [key: number]: string } = {
      [Stage.PersonalInfo]: 'Personal Details',
      [Stage.Education]: 'Education',
      [Stage.EmploymentHistory]: 'Experience',
      [Stage.Projects]: 'Projects',
      [Stage.SkillsCertifications]: 'Skills',
      [Stage.Review]: 'Review',
      [Stage.TemplateSelection]: 'Template',
      [Stage.Customization]: 'Customize',
      [Stage.Generation]: 'Generate',
    };
    return labels[stage] || 'Unknown';
  };

  const getStageProgress = (stage: Stage): number => {
    const totalStages = 9;
    return Math.round((stage / totalStages) * 100);
  };

  // Filter resumes based on search, template, and date
  const getFilteredResumes = (): Resume[] => {
    let filtered = [...resumes];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(resume =>
        resume.data.personal?.name?.toLowerCase().includes(query) ||
        resume.data.personal?.targetPosition?.toLowerCase().includes(query) ||
        resume.templateId?.toLowerCase().includes(query)
      );
    }

    // Template filter
    if (selectedTemplate !== 'all') {
      filtered = filtered.filter(resume => resume.templateId === selectedTemplate);
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      const filterDate = new Date();
      
      if (dateFilter === 'today') {
        filterDate.setHours(0, 0, 0, 0);
      } else if (dateFilter === 'week') {
        filterDate.setDate(now.getDate() - 7);
      } else if (dateFilter === 'month') {
        filterDate.setMonth(now.getMonth() - 1);
      }

      filtered = filtered.filter(resume => 
        new Date(resume.createdAt) >= filterDate
      );
    }

    return filtered;
  };

  // Calculate statistics
  const calculateStats = () => {
    const totalResumes = resumes.length;
    const totalJourneys = journeys.length;
    const inProgressCount = journeys.filter(j => j.status === 'in_progress').length;
    const completedCount = journeys.filter(j => j.status === 'completed').length;
    
    // Mock ATS average (in real app, would come from backend)
    const avgATSScore = totalResumes > 0 ? 85 : 0;

    return {
      totalResumes,
      totalJourneys,
      inProgressCount,
      completedCount,
      avgATSScore
    };
  };

  // Bulk actions
  const toggleResumeSelection = (resumeId: string) => {
    const newSelected = new Set(selectedResumes);
    if (newSelected.has(resumeId)) {
      newSelected.delete(resumeId);
    } else {
      newSelected.add(resumeId);
    }
    setSelectedResumes(newSelected);
  };

  const selectAllResumes = () => {
    const filteredResumes = getFilteredResumes();
    const paginatedIds = filteredResumes
      .slice((resumesPage - 1) * itemsPerPage, resumesPage * itemsPerPage)
      .map(r => r.id);
    setSelectedResumes(new Set(paginatedIds));
  };

  const clearSelection = () => {
    setSelectedResumes(new Set());
  };

  const handleBulkDownload = async () => {
    if (selectedResumes.size === 0) return;

    try {
      const resumeIds = Array.from(selectedResumes);
      for (const resumeId of resumeIds) {
        const resume = resumes.find(r => r.id === resumeId);
        if (resume) {
          await handleDownloadResume(resumeId, resume.data.personal.name);
          // Small delay between downloads
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      clearSelection();
      alert(`${selectedResumes.size} resume(s) downloaded successfully!`);
    } catch (err) {
      console.error('Bulk download failed:', err);
      alert('Some downloads may have failed. Please try again.');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedResumes.size === 0) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete ${selectedResumes.size} resume(s)? This action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      // In production, would have a bulk delete API
      const resumeIds = Array.from(selectedResumes);
      for (const resumeId of resumeIds) {
        // await api.deleteResume(resumeId);
        console.log(`Delete resume ${resumeId}`);
      }
      
      // Update local state
      setResumes(resumes.filter(r => !selectedResumes.has(r.id)));
      clearSelection();
      setBulkActionMode(false);
      alert(`${selectedResumes.size} resume(s) deleted successfully!`);
    } catch (err) {
      console.error('Bulk delete failed:', err);
      alert('Failed to delete some resumes. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Filter in-progress journeys
  const inProgressJourneys = journeys.filter(
    j => j.status === 'in_progress' && j.currentStage < Stage.Generation
  );

  // Get filtered resumes
  const filteredResumes = getFilteredResumes();
  const stats = calculateStats();

  // Pagination logic
  const paginatedJourneys = inProgressJourneys.slice(
    (journeysPage - 1) * itemsPerPage,
    journeysPage * itemsPerPage
  );
  const totalJourneyPages = Math.ceil(inProgressJourneys.length / itemsPerPage);

  const paginatedResumes = filteredResumes.slice(
    (resumesPage - 1) * itemsPerPage,
    resumesPage * itemsPerPage
  );
  const totalResumePages = Math.ceil(filteredResumes.length / itemsPerPage);

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div>
          <h1>Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}</h1>
          <p className="text-secondary">Build ATS-optimized resumes and prepare for interviews with AI-powered guidance</p>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <p>{error}</p>
          <button onClick={() => setError(null)} className="btn-ghost btn-sm">Dismiss</button>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon bg-primary">
            <FileText size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalResumes}</div>
            <div className="stat-label">Total Resumes</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon bg-success">
            <CheckCircle size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.completedCount}</div>
            <div className="stat-label">Completed</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon bg-warning">
            <Clock size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.inProgressCount}</div>
            <div className="stat-label">In Progress</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon bg-accent">
            <TrendingUp size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.avgATSScore}%</div>
            <div className="stat-label">Avg ATS Score</div>
          </div>
        </div>
      </div>

      <div className="dashboard-content">
        {/* Create New Resume */}
        <section className="dashboard-section">
          <h2 className="section-title">Create Resume</h2>
          <div className="create-options">
            <div className="card create-card" onClick={() => navigate('/upload')}>
              <div className="card-icon bg-primary">
                <Upload size={20} />
              </div>
              <div className="card-content">
                <h3>Upload Existing</h3>
                <p className="text-secondary">Optimize for ATS and get interview-ready</p>
              </div>
              <div className="card-badge badge-primary">Recommended</div>
            </div>

            <div className="card create-card" onClick={() => navigate('/new-journey')}>
              <div className="card-icon" style={{ backgroundColor: 'var(--color-neutral-100)', color: 'var(--color-neutral-600)' }}>
                <Mic size={20} />
              </div>
              <div className="card-content">
                <h3>Build from Scratch</h3>
                <p className="text-secondary">AI-guided resume building + interview prep</p>
              </div>
            </div>
          </div>
        </section>

        {/* In-Progress Journeys */}
        {inProgressJourneys.length > 0 && (
          <section className="dashboard-section">
            <div className="section-header">
              <h2 className="section-title">In Progress</h2>
              {inProgressJourneys.length > itemsPerPage && (
                <span className="text-tertiary text-sm">
                  {((journeysPage - 1) * itemsPerPage) + 1}-{Math.min(journeysPage * itemsPerPage, inProgressJourneys.length)} of {inProgressJourneys.length}
                </span>
              )}
            </div>
            <div className="cards-grid">
              {paginatedJourneys.map((journey) => (
                <div key={journey.id} className="card journey-card">
                  <div className="card-header">
                    <div className="flex items-center gap-2">
                      <Clock size={16} className="text-tertiary" />
                      <span className="text-sm text-secondary">{getStageLabel(journey.currentStage)}</span>
                    </div>
                    <span className="badge badge-warning">In Progress</span>
                  </div>
                  
                  <div className="journey-progress">
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${getStageProgress(journey.currentStage)}%` }}
                      />
                    </div>
                    <span className="text-xs text-tertiary">{getStageProgress(journey.currentStage)}% complete</span>
                  </div>

                  <div className="card-meta">
                    <span className="text-xs text-tertiary">
                      Started {new Date(journey.createdAt).toLocaleDateString()}
                    </span>
                  </div>

                  <button 
                    onClick={() => handleResumeJourney(journey)}
                    className="btn btn-primary"
                  >
                    <Play size={16} />
                    Continue
                  </button>
                </div>
              ))}
            </div>
            {totalJourneyPages > 1 && (
              <div className="pagination">
                <button 
                  className="btn btn-secondary btn-sm" 
                  onClick={() => setJourneysPage(p => Math.max(1, p - 1))}
                  disabled={journeysPage === 1}
                >
                  Previous
                </button>
                <span className="text-sm text-secondary">
                  Page {journeysPage} of {totalJourneyPages}
                </span>
                <button 
                  className="btn btn-secondary btn-sm"
                  onClick={() => setJourneysPage(p => Math.min(totalJourneyPages, p + 1))}
                  disabled={journeysPage === totalJourneyPages}
                >
                  Next
                </button>
              </div>
            )}
          </section>
        )}

        {/* Completed Resumes */}
        {resumes.length > 0 && (
          <section className="dashboard-section">
            <div className="section-header">
              <h2 className="section-title">Your Resumes</h2>
              <div className="section-actions">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="btn btn-secondary btn-sm"
                >
                  <Filter size={16} />
                  Filters
                </button>
                <button
                  onClick={() => {
                    setBulkActionMode(!bulkActionMode);
                    if (bulkActionMode) clearSelection();
                  }}
                  className="btn btn-secondary btn-sm"
                >
                  {bulkActionMode ? 'Cancel' : 'Select Multiple'}
                </button>
              </div>
            </div>

            {/* Search Bar */}
            <div className="search-container">
              <Search size={18} className="search-icon" />
              <input
                type="text"
                placeholder="Search by name, position, or template..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="search-clear">
                  <X size={16} />
                </button>
              )}
            </div>

            {/* Filters */}
            {showFilters && (
              <div className="filters-panel">
                <div className="filter-group">
                  <label className="filter-label">Template</label>
                  <select
                    value={selectedTemplate}
                    onChange={(e) => setSelectedTemplate(e.target.value)}
                    className="filter-select"
                  >
                    <option value="all">All Templates</option>
                    <option value="harvard">Harvard</option>
                    <option value="google">Google</option>
                    <option value="impact">Impact</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label className="filter-label">Date Range</label>
                  <select
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value as any)}
                    className="filter-select"
                  >
                    <option value="all">All Time</option>
                    <option value="today">Today</option>
                    <option value="week">Last 7 Days</option>
                    <option value="month">Last 30 Days</option>
                  </select>
                </div>

                <button
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedTemplate('all');
                    setDateFilter('all');
                  }}
                  className="btn btn-ghost btn-sm"
                >
                  Clear All
                </button>
              </div>
            )}

            {/* Bulk Actions Bar */}
            {bulkActionMode && selectedResumes.size > 0 && (
              <div className="bulk-actions-bar">
                <div className="bulk-info">
                  <span className="text-sm">{selectedResumes.size} selected</span>
                </div>
                <div className="bulk-buttons">
                  <button onClick={handleBulkDownload} className="btn btn-primary btn-sm">
                    <Download size={16} />
                    Download All
                  </button>
                  <button onClick={handleBulkDelete} className="btn btn-error btn-sm">
                    <Trash2 size={16} />
                    Delete Selected
                  </button>
                  <button onClick={clearSelection} className="btn btn-ghost btn-sm">
                    Clear Selection
                  </button>
                </div>
              </div>
            )}

            {/* Results Info */}
            {filteredResumes.length > 0 && (
              <div className="results-info">
                <span className="text-sm text-secondary">
                  Showing {((resumesPage - 1) * itemsPerPage) + 1}-{Math.min(resumesPage * itemsPerPage, filteredResumes.length)} of {filteredResumes.length} resume{filteredResumes.length !== 1 ? 's' : ''}
                </span>
                {bulkActionMode && (
                  <button onClick={selectAllResumes} className="btn btn-ghost btn-sm">
                    Select All on Page
                  </button>
                )}
              </div>
            )}

            {filteredResumes.length === 0 ? (
              <div className="empty-state">
                <FileText size={48} className="empty-icon" />
                <p className="text-secondary">No resumes match your filters</p>
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedTemplate('all');
                    setDateFilter('all');
                  }}
                  className="btn btn-secondary btn-sm"
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              <div className="cards-grid">
                {paginatedResumes.map((resume) => (
                  <div 
                    key={resume.id} 
                    className={`card resume-card ${selectedResumes.has(resume.id) ? 'selected' : ''} ${bulkActionMode ? 'selectable' : ''}`}
                    onClick={() => bulkActionMode && toggleResumeSelection(resume.id)}
                  >
                    {bulkActionMode && (
                      <div className="card-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedResumes.has(resume.id)}
                          onChange={() => toggleResumeSelection(resume.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    )}
                    <div className="card-header">
                    <div className="flex items-center gap-2">
                      <FileText size={16} className="text-tertiary" />
                      <span className="font-medium">{resume.data.personal?.name || 'Untitled'}</span>
                    </div>
                    <span className="badge badge-success">
                      <CheckCircle size={12} />
                      Complete
                    </span>
                  </div>

                  <div className="card-meta">
                    <p className="text-sm text-secondary">
                      {resume.data.personal?.targetPosition || 'No target position'}
                    </p>
                    <span className="text-xs text-tertiary">
                      {new Date(resume.createdAt).toLocaleDateString()}
                    </span>
                  </div>

                  <div className="resume-actions">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewResume(resume.id);
                      }}
                      className="btn btn-ghost btn-sm"
                    >
                      <Eye size={14} />
                      View
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCheckATS(resume.id);
                      }}
                      className="btn btn-ghost btn-sm"
                    >
                      <BarChart3 size={14} />
                      ATS Score
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadResume(resume.id, resume.data.personal?.name || 'resume');
                      }}
                      className="btn btn-ghost btn-sm"
                    >
                      <Download size={14} />
                      Download
                    </button>
                  </div>
                </div>
              ))}
              </div>
            )}

            {filteredResumes.length > 0 && totalResumePages > 1 && (
              <div className="pagination">
                <button 
                  className="btn btn-secondary btn-sm" 
                  onClick={() => setResumesPage(p => Math.max(1, p - 1))}
                  disabled={resumesPage === 1}
                >
                  Previous
                </button>
                <span className="text-sm text-secondary">
                  Page {resumesPage} of {totalResumePages}
                </span>
                <button 
                  className="btn btn-secondary btn-sm"
                  onClick={() => setResumesPage(p => Math.min(totalResumePages, p + 1))}
                  disabled={resumesPage === totalResumePages}
                >
                  Next
                </button>
              </div>
            )}
          </section>
        )}

        {/* Empty State */}
        {inProgressJourneys.length === 0 && resumes.length === 0 && (
          <div className="empty-state">
            <FileText size={48} className="text-tertiary" />
            <h3>No resumes yet</h3>
            <p className="text-secondary">Create your first professional resume</p>
            <button onClick={() => navigate('/upload')} className="btn btn-primary mt-4">
              <Plus size={16} />
              Get Started
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
